import { ReactElement } from 'react';

import { CONFIGPARAMTYPES } from '../../visual-analytics/helpers/configParams';
import { TpETLComponentTypeConfigParamDef } from '../etl-component-types/interfaces';
import { getDryRunErrorsForComponent } from '../etlDryRun';
import { TpETLPipelineComponent, TpETLDryRun } from '../interfaces';
import { getETLComponentInputObjectTable, getETLComponentInputObjectMatrixColumns } from '../utils';
import { TpMatrixColumnType } from '../etl-data-types/interfaces';

import styles from './editETLComponentForm.module.scss';
import ParamSet from './ParamSet';

interface TpETLComponentParameter {
  dryRun: TpETLDryRun;
  comp: TpETLPipelineComponent;
  paramDef: TpETLComponentTypeConfigParamDef;
  updateParam: (paramId: string, newValue: any) => void;
}

interface TpETLOptionListParameter {
  options: { id: string; name: string }[];
  comp: TpETLPipelineComponent;
  paramDef: TpETLComponentTypeConfigParamDef;
  updateParam: (paramId: string, newValue: any) => void;
}

interface TpRenderParamInfo {
  renderComp: any;
  description?: string;
}

function renderParameterBoolean({ dryRun, comp, paramDef, updateParam }: TpETLComponentParameter): TpRenderParamInfo {
  const paramValue = comp.params[paramDef.id] ?? paramDef.paramType.defaultValue;
  const description = paramDef.description;

  const renderComp = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
      <span>{paramDef.paramType.label as string} : </span>
      <input
        type="checkbox"
        checked={paramValue}
        onChange={(ev) => {
          updateParam(paramDef.id, ev.target.checked);
        }}
      />
    </div>
  );
  return { renderComp, description };
}

function renderParameterNumerical({ dryRun, comp, paramDef, updateParam }: TpETLComponentParameter): TpRenderParamInfo {
  const paramValue = comp.params[paramDef.id] ?? paramDef.paramType.defaultValue;
  const description = paramDef.description;

  let renderComp = (
    <input
      type="number"
      value={paramValue}
      onChange={(ev) => {
        updateParam(paramDef.id, ev.target.value);
      }}
    />
  );
  if (paramDef.paramType.minVal !== undefined && paramDef.paramType.maxVal !== undefined) {
    const minVal = paramDef.paramType.minVal as number;
    const maxVal = paramDef.paramType.maxVal as number;
    renderComp = (
      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
        <span>{paramDef.paramType.minVal as number}</span>
        <input
          type="range"
          value={paramValue}
          min={minVal}
          max={maxVal}
          step={paramDef.paramType.intervalStep as number | undefined}
          onChange={(ev) => {
            const newValue = Number(ev.target.value);
            updateParam(paramDef.id, newValue);
          }}
          style={{
            accentColor: 'var(--base-color)',
            width: '150px',
            height: '4px',
            WebkitAppearance: 'none',
            appearance: 'none',
            cursor: 'pointer',
          }}
        />
        <span>{maxVal}</span>
        <span style={{ fontWeight: 'bold', marginLeft: '50px' }}>
          <input
            type="number"
            min={minVal}
            max={maxVal}
            value={paramValue}
            onChange={(ev) => {
              const newValue = Number(ev.target.value);

              // Ensure the new value is within the specified range
              if (newValue >= minVal && newValue <= maxVal) {
                updateParam(paramDef.id, newValue);
              } else if (newValue < minVal) {
                updateParam(paramDef.id, minVal);
              } else if (newValue > maxVal) {
                updateParam(paramDef.id, maxVal);
              }
            }}
          />
        </span>
      </div>
    );
  }
  return { renderComp, description };
}

function renderParameterString({ dryRun, comp, paramDef, updateParam }: TpETLComponentParameter): TpRenderParamInfo {
  const paramValue = comp.params[paramDef.id] ?? paramDef.paramType.defaultValue;
  let description = paramDef.description;

  if (comp.extraParamObjectIds.length > 0) {
    const paramList = comp.extraParamObjectIds.map((extraParam, idx) => `$param${idx + 1}`).join(', ');
    description += ` You can enter ${paramList} as placeholders for using additional parameter(s).`;
  }
  const renderComp = (
    <input
      value={paramValue}
      onChange={(ev) => {
        updateParam(paramDef.id, ev.target.value);
      }}
    />
  );
  return { renderComp, description };
}

function renderParameterText({ dryRun, comp, paramDef, updateParam }: TpETLComponentParameter): TpRenderParamInfo {
  const paramValue = comp.params[paramDef.id];
  let description = paramDef.description;

  if (comp.extraParamObjectIds.length > 0) {
    const paramList = comp.extraParamObjectIds.map((extraParam, idx) => `$param${idx + 1}`).join(', ');
    description += ` You can enter ${paramList} as placeholders for using additional parameter(s).`;
  }
  const renderComp = (
    <textarea
      value={paramValue}
      onChange={(ev) => {
        updateParam(paramDef.id, ev.target.value);
      }}
    />
  );
  return { renderComp, description };
}

function renderParameterMultipleChoice({
  dryRun,
  comp,
  paramDef,
  updateParam,
}: TpETLComponentParameter): TpRenderParamInfo {
  const paramValue = comp.params[paramDef.id] ?? paramDef.paramType.defaultValue;
  const description = paramDef.description;
  const options = paramDef.paramType.options as { id: string; name: string }[];

  const renderComp = (
    <select
      value={paramValue}
      onChange={(ev) => {
        updateParam(paramDef.id, ev.target.value);
      }}
    >
      <option key="_empty_" value={''}>
        ---- Select Option ----
      </option>
      {options.map((option: any) => (
        <option key={option.id} value={option.id}>
          {option.name}
        </option>
      ))}
    </select>
  );
  return { renderComp, description };
}

function renderParameterMultipleChoiceList({
  dryRun,
  comp,
  paramDef,
  updateParam,
}: TpETLComponentParameter): TpRenderParamInfo {
  const renderComp = renderOptionList({
    options: paramDef.paramType.options as { id: string; name: string }[],
    comp,
    paramDef,
    updateParam,
  });
  return { renderComp, description: paramDef.description };
}

function renderParameterNewTableColumn({
  dryRun,
  comp,
  paramDef,
  updateParam,
}: TpETLComponentParameter): TpRenderParamInfo {
  let paramValue = comp.params[paramDef.id];
  const description = paramDef.description;

  if (!paramValue) paramValue = { identifier: '', name: '', description: '' };
  const renderComp = (
    <ParamSet
      params={[
        {
          name: 'Identifier',
          component: (
            <input
              value={paramValue.identifier}
              onChange={(ev) => {
                updateParam(paramDef.id, { ...paramValue, identifier: ev.target.value });
              }}
            />
          ),
        },
        {
          name: 'Name',
          component: (
            <input
              value={paramValue.name}
              onChange={(ev) => {
                updateParam(paramDef.id, { ...paramValue, name: ev.target.value });
              }}
            />
          ),
        },
        {
          name: 'Description',
          component: (
            <input
              value={paramValue.description}
              onChange={(ev) => {
                updateParam(paramDef.id, { ...paramValue, description: ev.target.value });
              }}
            />
          ),
        },
      ]}
    />
  );

  return { renderComp, description };
}

function renderSelectColumn({ columns, comp, paramDef, updateParam }: TpETLComponentColumnsParameter): ReactElement {
  const paramValue = comp.params[paramDef.id];

  return (
    <select
      value={paramValue}
      onChange={(ev) => {
        updateParam(paramDef.id, ev.target.value);
      }}
    >
      <option key="_empty_" value={''}>
        - Select -
      </option>
      {columns.map((col) => (
        <option key={col.identifier} value={col.identifier}>
          {col.name}
        </option>
      ))}
    </select>
  );
}

function renderParameterExistingTableColumn({
  dryRun,
  comp,
  paramDef,
  updateParam,
}: TpETLComponentParameter): TpRenderParamInfo {
  const table = getETLComponentInputObjectTable(dryRun, comp, paramDef.paramType.inputTableId as string);
  const description = paramDef.description;

  const renderComp = renderSelectColumn({
    columns: table.columns,
    comp,
    paramDef,
    updateParam,
  });
  return { renderComp, description };
}

function renderParameterExistinMatrixColumn({
  dryRun,
  comp,
  paramDef,
  updateParam,
}: TpETLComponentParameter): TpRenderParamInfo {
  const inputId = paramDef.paramType.inputMatrixId as string;
  const columnType = paramDef.paramType.matrixColumnType as TpMatrixColumnType;
  const columns = getETLComponentInputObjectMatrixColumns(dryRun, comp, inputId, columnType);
  const description = paramDef.description;

  const renderComp = renderSelectColumn({
    columns: columns,
    comp,
    paramDef,
    updateParam,
  });
  return { renderComp, description };
}

function renderOptionList({ options, comp, paramDef, updateParam }: TpETLOptionListParameter): ReactElement {
  const columnIds = options.map((option) => option.id);
  let selectedColumnIds = (comp.params[paramDef.id] || paramDef.paramType.defaultValue || []) as string[];
  // we do this filtering to remove columns that no longer exist because of upstream pipeline changes.
  selectedColumnIds = selectedColumnIds.filter((colId) => columnIds.includes(colId));
  const anySelected = selectedColumnIds.length > 0;
  const allSelected = selectedColumnIds.length === columnIds.length;

  // Indeterminate state from https://github.com/facebook/react/issues/1798#issuecomment-256816975
  return (
    <div>
      <div className={styles.selectAllCheckbox}>
        <input
          type="checkbox"
          id={`${comp.id}_${paramDef.id}__selectAll`}
          checked={allSelected}
          ref={(elem) => elem && (elem.indeterminate = anySelected && !allSelected)}
          onChange={() => {
            if (!anySelected) selectedColumnIds = columnIds;
            else selectedColumnIds = [];
            updateParam(paramDef.id, [...selectedColumnIds]);
          }}
        />
        <label htmlFor={`${comp.id}_${paramDef.id}__selectAll`}>{!anySelected ? 'Select all' : 'Deselect all'}</label>
      </div>
      {options.map((option) => (
        <div key={option.id} className={styles.columnCheckbox}>
          <input
            type="checkbox"
            id={`${comp.id}_${paramDef.id}_${option.id}`}
            checked={selectedColumnIds.indexOf(option.id) >= 0}
            onChange={() => {
              if (selectedColumnIds.indexOf(option.id) < 0) selectedColumnIds.push(option.id);
              else selectedColumnIds = selectedColumnIds.filter((el) => el != option.id);
              updateParam(paramDef.id, [...selectedColumnIds]);
            }}
          />
          <label htmlFor={`${comp.id}_${paramDef.id}_${option.id}`}>{option.name}</label>
        </div>
      ))}
    </div>
  );
}

function renderParameterExistingTableColumnList({
  dryRun,
  comp,
  paramDef,
  updateParam,
}: TpETLComponentParameter): TpRenderParamInfo {
  const table = getETLComponentInputObjectTable(dryRun, comp, paramDef.paramType.inputTableId as string);
  const description = paramDef.description;

  const renderComp = renderOptionList({
    options: table.columns.map((col) => ({ id: col.identifier, name: col.name })),
    comp,
    paramDef,
    updateParam,
  });
  return { renderComp, description };
}

function renderParameterExistingMatrixColumnList({
  dryRun,
  comp,
  paramDef,
  updateParam,
}: TpETLComponentParameter): TpRenderParamInfo {
  const inputId = paramDef.paramType.inputMatrixId as string;
  const columnType = paramDef.paramType.matrixColumnType as TpMatrixColumnType;
  const columns = getETLComponentInputObjectMatrixColumns(dryRun, comp, inputId, columnType);

  const renderComp = renderOptionList({
    options: columns.map((col) => ({ id: col.identifier, name: col.name })),
    comp,
    paramDef,
    updateParam,
  });
  return { renderComp, description: paramDef.description };
}

const renderParamFactory: { [paramType: string]: any } = {};
renderParamFactory[CONFIGPARAMTYPES.String] = renderParameterString;
renderParamFactory[CONFIGPARAMTYPES.Text] = renderParameterText;
renderParamFactory[CONFIGPARAMTYPES.Numerical] = renderParameterNumerical;
renderParamFactory[CONFIGPARAMTYPES.Boolean] = renderParameterBoolean;
renderParamFactory['multipleChoice'] = renderParameterMultipleChoice;
renderParamFactory['multipleChoiceList'] = renderParameterMultipleChoiceList;
renderParamFactory['newTableColumn'] = renderParameterNewTableColumn;
renderParamFactory['existingTableColumn'] = renderParameterExistingTableColumn;
renderParamFactory['existingMatrixColumn'] = renderParameterExistinMatrixColumn;
renderParamFactory['existingTableColumnList'] = renderParameterExistingTableColumnList;
renderParamFactory['existingMatrixColumnList'] = renderParameterExistingMatrixColumnList;

function ETLComponentParameter({ dryRun, comp, paramDef, updateParam }: TpETLComponentParameter) {
  const errors = getDryRunErrorsForComponent(dryRun, comp.id).filter((error) => error.paramId == paramDef.id);

  if (!renderParamFactory[paramDef.paramType.configParamTypeId])
    throw `Invalid parameter type ${paramDef.paramType.configParamTypeId}`;
  const { renderComp, description } = renderParamFactory[paramDef.paramType.configParamTypeId]({
    dryRun,
    comp,
    paramDef,
    updateParam,
  });

  return (
    <div>
      <h3>{paramDef.name}</h3>
      {description && <div style={{ margin: '7px 0' }}>{description}</div>}
      {errors.length > 0 && (
        <div className={styles.errors}>
          {errors.map((error, idx) => (
            <div key={`idx_${idx}`}>{error.message}</div>
          ))}
        </div>
      )}

      {renderComp}
    </div>
  );
}

export default ETLComponentParameter;
